/**
 * Board — DndContext over the stage columns. Drop on a column = move the lead
 * (optimistic). Mouse needs 6 px of travel, touch a 200 ms hold, so taps and
 * scrolls never start a drag. MouseSensor (not PointerSensor) on purpose:
 * PointerSensor also receives touch-derived pointer events, which let a plain
 * vertical swipe lift a card after 6 px and bypassed the hold (QA stage 1).
 */
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { StageAdmin } from '@/features/stages/use-stage-admin';
import { AddStageColumn } from './AddStageColumn';
import { LeadCardView } from './LeadCard';
import { StageColumn } from './StageColumn';
import type { LeadCard, Stage } from './types';

export function groupByStage(stages: Stage[], leads: LeadCard[]): Map<string, LeadCard[]> {
  const map = new Map<string, LeadCard[]>(stages.map((s) => [s.id, []]));
  for (const lead of leads) {
    const bucket = map.get(lead.stage_id);
    if (bucket) bucket.push(lead);
  }
  return map;
}

export function Board({
  stages,
  leads,
  admin,
  onOpenLead,
  onMove,
}: {
  stages: Stage[];
  leads: LeadCard[];
  admin: StageAdmin | null;
  onOpenLead: (id: string) => void;
  onMove: (leadId: string, toStageId: string) => void;
}) {
  const [active, setActive] = useState<LeadCard | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const grouped = useMemo(() => groupByStage(stages, leads), [stages, leads]);

  const onDragStart = (e: DragStartEvent) => {
    const lead = (e.active.data.current as { lead?: LeadCard } | undefined)?.lead;
    setActive(lead ?? null);
  };
  const onDragEnd = (e: DragEndEvent) => {
    setActive(null);
    const lead = (e.active.data.current as { lead?: LeadCard } | undefined)?.lead;
    const to = e.over?.id;
    if (lead && typeof to === 'string' && to !== lead.stage_id) onMove(lead.id, to);
  };

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
      <div className="scrollbar-thin flex h-full gap-3 overflow-x-auto overflow-y-hidden px-4 pb-4 sm:px-6" data-testid="board">
        {stages.map((stage, i) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            leads={grouped.get(stage.id) ?? []}
            index={i}
            total={stages.length}
            admin={admin}
            onOpenLead={onOpenLead}
          />
        ))}
        {admin && <AddStageColumn admin={admin} afterStageId={stages[stages.length - 1]?.id} />}
      </div>
      <DragOverlay dropAnimation={null}>
        {active && (
          <div className="w-[256px]">
            <LeadCardView lead={active} lifted />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
